import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginRequest } from '../types';
import { apiService } from './api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginRequest) => Promise<User>;
  logout: () => void;
  isAuthenticated: boolean;
  isKalotteUser: boolean;
  isViewer: boolean;
  isOperator: boolean;
  canAccessProduction: boolean;
  canWrite: boolean;
  canAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        try {
          // Verify token is still valid
          const currentUser = await apiService.getCurrentUser();
          setUser(currentUser);
          localStorage.setItem('user', JSON.stringify(currentUser));
        } catch (error) {
          // Token is invalid
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginRequest) => {
    const authResponse = await apiService.login(credentials);
    localStorage.setItem('token', authResponse.access_token);

    const currentUser = await apiService.getCurrentUser();
    setUser(currentUser);
    localStorage.setItem('user', JSON.stringify(currentUser));
    return currentUser;
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const isKalotteUser = user?.role === 'KALOTTE';
  const isOperator = user?.role === 'KALOTTE_OPERATOR';

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    isKalotteUser,
    isViewer: user?.role === 'KALOTTE_VIEWER',
    isOperator,
    canAccessProduction: isKalotteUser || isOperator,
    canWrite: user?.role === 'KALOTTE' || user?.role === 'RUBY',
    canAdmin: isKalotteUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
